import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Checkbox, List, ListItem, ListItemText } from '@mui/material';
import { validateStateChange } from 'features/document/edit/documents/actionCreators';
import { computePartnerFileNextStates, updatePartnerFileState } from 'features/partnerFile/actionCreators';
import { getPartnerFileHeader, getPartnerFileNextStates } from 'features/partnerFile/selectors';
import { isMandatory, validationEngine } from 'utils/simple-validations';
import { getCommentsConfig } from 'actions/actionCreators/offer/offerStatesActions';
import documentTypeIds from 'constants/documentTypeIds';
import { changeValueInArray } from 'utils/functions';
import { Autocomplete, Button, Dialog, TextField } from '@totalsoft/rocket-ui';
import { BoxActionsWrapper } from 'components/common/CommonStyles';

const PartnerFileStatesSelectionListContainer = ({ documentId, siteId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [selectedState, setSelectedState] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [validationInfo, setValidationInfo] = useState({ isValid: true });
  const [awaitingStateChange, setAwaitingStateChange] = useState(false);
  const [awaitingComment, setAwaitingComment] = useState(false);
  const [comment, setComment] = useState({ commentText: '', reasons: [] });
  const { loaded: partnerFileHeaderLoaded, content: partnerFileHeader } = useSelector(
    state => getPartnerFileHeader(state, documentId, siteId),
    shallowEqual
  );
  const { loaded: nextStatesLoaded, content: nextStates } = useSelector(
    state => getPartnerFileNextStates(state, documentId, siteId),
    shallowEqual
  );
  const { isLoaded: commentsConfigIsLoaded, list: commentsConfig } = useSelector(state => state.dictionaries.commentsConfig, shallowEqual);

  const commentConfigForNextState = useMemo(
    () => commentsConfig?.find(a => a?.stateId === selectedState.stateId),
    [commentsConfig, selectedState]
  );

  const closeModal = useCallback(() => {
    setShowModal(false);
    setAwaitingStateChange(false);
    setAwaitingComment(false);
    setSelectedState({});
  }, []);

  const onStateChangeValidatedCallback = useCallback(
    nextSelectedState => {
      if (
        nextSelectedState?.stateId === selectedState.stateId ||
        (nextSelectedState.requiresConfirmation && !window.confirm(t('ConfirmStateChange')))
      )
        return;

      const cfg = commentsConfig.find(a => a.stateId === nextSelectedState.stateId);
      setComment({
        commentText: '',
        reasons:
          cfg?.reasons?.map(reason => {
            return { ...reason, selected: false };
          }) ?? []
      });

      if (cfg?.isVisible) setAwaitingComment(true);

      setSelectedState(nextSelectedState);
      setAwaitingStateChange(true);
    },
    [t, selectedState.stateId, commentsConfig]
  );

  const handleSelectState = useCallback(
    nextSelectedState => {
      dispatch(
        validateStateChange(documentId, siteId, partnerFileHeader.documentStateId, nextSelectedState?.stateId, () =>
          onStateChangeValidatedCallback(nextSelectedState)
        )
      );
    },
    [dispatch, documentId, siteId, onStateChangeValidatedCallback, partnerFileHeader.documentStateId]
  );

  const onCommentChange = value => {
    setComment({ commentText: value, reasons: comment.reasons });
  };

  const reasonChange = reason => event => {
    let selected = event.target.checked;
    let res = changeValueInArray(comment.reasons, 'id', reason.id, 'selected', selected);
    setComment({ commentText: comment.commentText, reasons: res });
  };

  useEffect(() => {
    if (!commentsConfigIsLoaded) dispatch(getCommentsConfig(documentTypeIds.PartnerFile));
  }, [commentsConfigIsLoaded, dispatch]);

  useEffect(() => {
    const validationRules =
      commentConfigForNextState?.isVisible && commentConfigForNextState.commentRequired ? { commentText: [isMandatory()] } : {};
    const validationInfo = validationEngine.validate(comment, validationRules);
    setValidationInfo({
      ...validationInfo
    });
  }, [comment, commentsConfig, selectedState, commentConfigForNextState]);

  useEffect(() => {
    if (partnerFileHeaderLoaded && selectedState === null)
      setSelectedState({ stateId: partnerFileHeader.documentStateId, stateName: partnerFileHeader.documentStateName });
  }, [partnerFileHeader, partnerFileHeaderLoaded, selectedState]);

  useEffect(() => {
    if (!nextStatesLoaded) dispatch(computePartnerFileNextStates(documentId, siteId));
  }, [documentId, siteId, nextStatesLoaded, dispatch]);

  useEffect(() => {
    if (awaitingStateChange && !awaitingComment && selectedState.stateId !== partnerFileHeader.documentStateId) {
      if (showModal) closeModal();
      else setAwaitingStateChange(false);
      dispatch(
        updatePartnerFileState(
          documentId,
          siteId,
          selectedState.stateId,
          comment.commentText,
          comment.reasons?.filter(x => x.selected).map(a => a.id) ?? []
        )
      );
    }
    if (awaitingStateChange && awaitingComment && showModal === false) {
      setShowModal(true);
    }
  }, [
    awaitingStateChange,
    awaitingComment,
    selectedState,
    partnerFileHeader.documentStateId,
    showModal,
    comment,
    dispatch,
    closeModal,
    documentId,
    siteId
  ]);

  return (
    <>
      <Autocomplete
        valueKey='stateId'
        labelKey='stateName'
        value={{ stateId: partnerFileHeader.documentStateId, stateName: partnerFileHeader.documentStateName }}
        onChange={handleSelectState}
        isClearable={false}
        isSearchable={false}
        options={nextStates}
      />
      <Dialog
        open={showModal}
        onClose={closeModal}
        fullWidth
        maxWidth='xs'
        aria-labelledby='comment-dialog-title'
        title={t('OfferEdit.States.CommentsTitle')}
        content={
          <>
            <TextField
              id={'offerHeader_state_comment'}
              required={commentConfigForNextState?.commentRequired}
              fullWidth
              value={comment.commentText}
              onChange={onCommentChange}
              label={t('OfferEdit.States.Comment')}
              multiline
              error={!!validationInfo.commentText}
              helperText={validationInfo.commentText}
              inputProps={{
                maxLength: 255
              }}
            />
            {comment.reasons && comment.reasons.length > 0 && (
              <List>
                {comment.reasons.map((reason, idx) => (
                  <ListItem key={reason.id} disableGutters>
                    <Checkbox
                      checked={reason.selected}
                      onChange={reasonChange(reason)}
                      id={'offerHeader_state_comment_reason_' + idx + 'selected'}
                    />
                    <ListItemText primary={reason.description} id={'offerHeader_state_comment_reason_' + idx + 'description'} />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        }
        actions={
          <BoxActionsWrapper forDialog>
            <Button id={'offerHeader_state_comment_saveBtn'} onClick={() => setAwaitingComment(false)} disabled={!validationInfo.isValid}>
              {t('Save')}
            </Button>
            <Button variant='outlined' id={'offerHeader_state_comment_closeBtn'} onClick={closeModal}>
              {t('Close')}
            </Button>
          </BoxActionsWrapper>
        }
      />
    </>
  );
};

PartnerFileStatesSelectionListContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired
};

export default PartnerFileStatesSelectionListContainer;
